import React, { createElement, useCallback, useEffect, useRef } from 'react';

import { EventScope, useLogEvent, useShallowStableEvent } from './core';

import type { Event } from './core';
import type { ReactNode } from 'react';

export const useInViewedEvent = (target: Event['target']) => {
  const inViewDedupedRef = useRef(new Set());
  // useInViewedEvent 需要有 key 來去重複的機制，避免 UI 元素消失後「再次」出現在畫面，所以使用 Set 來處理
  const handleLogEvent = useLogEvent('inView', target);

  const log = useCallback(
    (props: Event['props'], { key }: { key?: string } = {}) => {
      if (key && inViewDedupedRef.current.has(key)) {
        return;
      }

      handleLogEvent(props);

      key && inViewDedupedRef.current.add(key);
    },
    [handleLogEvent],
  );

  return log;
};

const EMPTY_PROPS: Event['props'] = {};

type EventInViewProps = {
  props?: Event['props'];
  target: Event['target'];
  children: ReactNode;
  isReady?: boolean;
};

export const EventInView = ({
  children,
  target,
  props = EMPTY_PROPS,
  isReady = true,
}: EventInViewProps) => {
  const inView = useInViewedEvent(target);
  const stabledEvent = useShallowStableEvent({ props });

  useEffect(() => {
    if (!isReady) return;
    if (!stabledEvent.props) return;

    // target 必填且在同一個實體內固定，直接拿它當去重 key：
    // 同一個 mount 週期內只送一次，擋掉上層 context 抖動或 re-render 造成的重複上報。
    inView(stabledEvent.props, { key: target });
  }, [target, inView, isReady, stabledEvent]);

  return children;
};

export const useClickEvent = (target: Event['target']) => {
  const handleClickEvent = useLogEvent('click', target);

  const log = useCallback(handleClickEvent, [handleClickEvent]);

  return log;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

type PropsOf<T extends React.ElementType> = React.ComponentPropsWithoutRef<T>;

type OnClickOf<T extends React.ElementType> =
  PropsOf<T> extends { onClick?: infer TOnClick }
    ? Extract<TOnClick, AnyFunction>
    : AnyFunction;

/**
 * 用法是 Hoc
 * const EventTrackedHTMLButton = withTrackedClickEvent('button')
 * const EventTrackedDialogCloseButton = withTrackedClickEvent(DialogCloseButton)
 *
 * 使用 withTrackedClickEvent 之後的 Component 有幾個好處，原本需要
 * const logConfirm = useClickEvent('確認');
 * <button onClick={() => {logConfirm() }}></button>
 *
 * 現在只需要
 * const EventTrackedHTMLButton = withTrackedClickEvent('button')
 * <EventTrackedHTMLButton eventTarget={'確認'} />
 *
 * 乍看之下縮短不少
 *
 * 另一個層面是。原本會需要獨立一層 Component，這裡 useClickEvent 才吃得到 Scope 的 Context
 * 但使用 HOC 過後的 Button 應該可以避免掉這個問題
 *
 *
 * <EventScope eventProps={{ scope: '計費週期彈窗' }}>
 *  <EventTrackedHTMLButton eventTarget={'確認'} />
 * </EventScope>
 *
 * 上面的範例是可以直接用的，不用再把 EventScope 獨立一層 Component
 */
export const withTrackedClickEvent = <TElement extends React.ElementType>(
  Element: TElement,
) => {
  type ElementProps = PropsOf<TElement>;
  type OnClick = OnClickOf<TElement>;

  type TrackedProps = Omit<ElementProps, 'onClick'> & {
    eventTarget: Event['target'];
    eventProps?: Event['props'];
    onClick?: OnClick;
  };

  return (rawProps: TrackedProps) => {
    const { eventTarget, eventProps, onClick, ...props } = rawProps;
    const log = useClickEvent(eventTarget);

    const onElementClick = ((...args: Parameters<OnClick>) => {
      onClick?.(...args);
      log(eventProps);
    }) as OnClick;

    const elementProps = {
      onClick: onElementClick,
      ...props,
    } as ElementProps;

    return createElement(Element, elementProps);
  };
};

type EventPageInViewProps = Pick<
  EventInViewProps,
  'children' | 'isReady' | 'props'
> & {
  pageTarget: EventInViewProps['target'];
};

/**
 * 很常出現一個 pattern 是，event name 為 PageName_PageName_inView 的事件。在這樣的結構下就很長出現一個狀況是
 * const Comp = () => <EventInView target="設定Dialog">{children}</EventInView>
 * const Scoped = () => <EventScope page="設定Dialog" ><Comp/></EventScope>
 *
 * 但這樣寫真的太麻煩了，幾乎所有的 Page 都有一個 Inview 的事件。EventPageInView 可以用更方便的形式呈現，避免寫兩次 target 跟 page
 * <EventPageInView pageTarget="設定Dialog"/>
 */
export const EventPageInView = ({
  pageTarget,
  props,
  children,
  isReady,
}: EventPageInViewProps) => {
  return (
    <EventScope.Page page={pageTarget} props={props}>
      <EventInView target={pageTarget} isReady={isReady} props={props}>
        {children}
      </EventInView>
    </EventScope.Page>
  );
};

type EventScopeInViewProps = Pick<
  EventInViewProps,
  'children' | 'isReady' | 'props'
> & {
  scopeTarget: EventInViewProps['target'];
};

/**
 * 跟 EventPageInView 是同樣的 pattern，差別在於 EventPageInView 佔用的是 page 欄位，
 * 給「路由層級」的 Page 用；EventScopeInView 佔用的是 scope 欄位，給彈窗、Dialog 這類
 * 「會被巢狀在某個 Page 底下」的元件用，避免它們的 page 把外層真正的 Page 蓋掉。
 *
 * <EventScopeInView scopeTarget="計費週期彈窗"/>
 */
export const EventScopeInView = ({
  scopeTarget,
  props,
  children,
  isReady,
}: EventScopeInViewProps) => {
  return (
    <EventScope eventProps={{ ...props, scope: scopeTarget }}>
      <EventInView target={scopeTarget} props={props} isReady={isReady}>
        {children}
      </EventInView>
    </EventScope>
  );
};
